import { computeLineSubtotal, type QuoteModeValue } from '@devizy/shared';

import type { QuoteViewExpectation, QuoteViewLine } from './types';

export interface QuoteGroup {
  parent: QuoteViewExpectation;
  lines: QuoteViewLine[];
}

export const ORPHANS_GROUP_ID = '__orphans__';

export function groupByParent(
  expectations: QuoteViewExpectation[],
  lines: QuoteViewLine[],
  orphansLabel: string,
): QuoteGroup[] {
  const parents = expectations
    .filter((e) => e.level === 'PARENT')
    .sort((a, b) => a.order - b.order);
  const children = expectations.filter((e) => e.level === 'CHILD');
  const childToParent = new Map<string, string>();
  for (const child of children) {
    if (child.parentId) childToParent.set(child.id, child.parentId);
  }
  const groups: QuoteGroup[] = parents.map((parent) => ({ parent, lines: [] }));
  const groupIndexByParentId = new Map<string, number>();
  groups.forEach((group, idx) => groupIndexByParentId.set(group.parent.id, idx));
  const orphanLines: QuoteViewLine[] = [];
  for (const line of lines) {
    if (line.expectationId) {
      const parentId = childToParent.get(line.expectationId) ?? line.expectationId;
      const idx = groupIndexByParentId.get(parentId);
      if (idx !== undefined) {
        groups[idx]!.lines.push(line);
        continue;
      }
    } else if (line.additionParentId) {
      const idx = groupIndexByParentId.get(line.additionParentId);
      if (idx !== undefined) {
        groups[idx]!.lines.push(line);
        continue;
      }
    }
    orphanLines.push(line);
  }
  if (orphanLines.length > 0 && groups.length > 0) {
    groups[groups.length - 1]!.lines.push(...orphanLines);
  } else if (orphanLines.length > 0) {
    groups.push({
      parent: {
        id: ORPHANS_GROUP_ID,
        parentId: null,
        level: 'PARENT',
        title: orphansLabel,
        description: '',
        order: 999,
      },
      lines: orphanLines,
    });
  }
  return groups;
}

export interface SectionStats {
  pricedCount: number;
  toCompleteCount: number;
  additionCount: number;
  modificationCount: number;
  deletionCount: number;
}

export function computeSectionStats(lines: QuoteViewLine[]): SectionStats {
  const stats: SectionStats = {
    pricedCount: 0,
    toCompleteCount: 0,
    additionCount: 0,
    modificationCount: 0,
    deletionCount: 0,
  };
  for (const line of lines) {
    if (!line.isProposedForDeletion) {
      if (line.isPricingComplete) stats.pricedCount += 1;
      else stats.toCompleteCount += 1;
    }
    if (line.discrepancyType === 'ADDITION') stats.additionCount += 1;
    else if (line.discrepancyType === 'MODIFICATION') stats.modificationCount += 1;
    else if (line.discrepancyType === 'DELETION_PROPOSED') stats.deletionCount += 1;
  }
  return stats;
}

export function generateLocalLineId(): string {
  return `local_line_${Math.random().toString(36).slice(2, 10)}`;
}

function resolveDiscrepancyType(
  line: QuoteViewLine,
  expectations: QuoteViewExpectation[],
): QuoteViewLine['discrepancyType'] {
  if (line.expectationId === null && !line.isProposedForDeletion) return 'ADDITION';
  if (line.isProposedForDeletion) return 'DELETION_PROPOSED';
  const expectation = expectations.find((e) => e.id === line.expectationId);
  if (!expectation) return line.discrepancyType;
  const sameTitle = line.bidderTitle.trim() === expectation.title.trim();
  const sameDescription = line.bidderDescription.trim() === expectation.description.trim();
  return sameTitle && sameDescription ? 'NONE' : 'MODIFICATION';
}

export function applyLinePatch(
  lines: QuoteViewLine[],
  targetIdx: number,
  patch: Partial<QuoteViewLine>,
  expectations: QuoteViewExpectation[],
  quoteMode: QuoteModeValue,
): QuoteViewLine[] {
  return lines.map((line, idx) => {
    if (idx !== targetIdx) return line;
    const merged: QuoteViewLine = { ...line, ...patch, mode: quoteMode };
    merged.discrepancyType = resolveDiscrepancyType(merged, expectations);
    const computed = computeLineSubtotal({
      mode: merged.mode,
      quantity: merged.quantity,
      unitPrice: merged.unitPrice,
      adjustment: merged.adjustment,
      isProposedForDeletion: merged.isProposedForDeletion,
    });
    merged.subtotal = computed.subtotal;
    merged.isPricingComplete = computed.isPricingComplete;
    return merged;
  });
}

export function buildAdditionLine(
  lines: QuoteViewLine[],
  parentId: string,
  quoteMode: QuoteModeValue,
): QuoteViewLine {
  return {
    id: generateLocalLineId(),
    expectationId: null,
    additionParentId: parentId,
    bidderTitle: '',
    bidderDescription: '',
    mode: quoteMode,
    quantity: null,
    unitPrice: null,
    adjustment: null,
    subtotal: null,
    isPricingComplete: false,
    discrepancyType: 'ADDITION',
    isProposedForDeletion: false,
    deletionReason: null,
    additionJustification: '',
    bidderComment: null,
    order: lines.length,
  };
}
